import {cva, type VariantProps, cn} from 'platform/shared-tw';

import {Children, isValidElement, type CSSProperties, type ReactNode, useMemo} from 'react';

import type {MiraType} from '../../types';

const viewportVariants = cva(
  'w-full overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden md:overflow-visible'
);

export const TABLE_GRID_COLUMNS_CLASS = '[grid-template-columns:46px_minmax(0,1fr)]';

const tableVariants = cva(`grid w-full ${TABLE_GRID_COLUMNS_CLASS} gap-y-[var(--spacing-1)]`);

const headVariants = cva('contents');

const bodyVariants = cva('contents');

const headerSpacerVariants = cva(
  'sticky left-0 z-10 bg-[var(--color-bg-primary)] pb-[var(--spacing-mira-md)] pt-[var(--spacing-mira-md)] md:static md:bg-transparent'
);

const headerCellsWrapperVariants = cva(
  'grid w-fit items-start gap-[var(--spacing-mira-md)] pb-[var(--spacing-mira-md)] pr-[var(--spacing-mira-md)] pt-[var(--spacing-mira-md)] pl-[var(--spacing-mira-md)] [grid-template-columns:var(--columns-template)] md:w-auto'
);

const headerCellVariants = cva(
  'flex min-w-0 items-start text-sm font-normal leading-5 text-[var(--color-text-tertiary)]',
  {
    variants: {
      align: {
        left: 'justify-start',
        center: 'justify-center',
        right: 'justify-end',
      },
    },
    defaultVariants: {
      align: 'left',
    },
  }
);

const rowVariants = cva('contents');

const iconWrapperVariants = cva(
  'sticky left-0 z-10 flex items-center justify-center overflow-hidden rounded-l-sm px-[var(--spacing-mira-md)] py-3 md:static',
  {
    variants: {
      type: {
        damage: 'mira-type-damage bg-[var(--mira-bg)] text-[var(--color-mira-red-70)]',
        service: 'mira-type-service bg-[var(--mira-bg)] text-[var(--color-mira-orange-70)]',
        suggestions: 'mira-type-suggestions bg-[var(--mira-bg)] text-[var(--color-mira-purple-70)]',
      },
    },
  }
);

const cellsWrapperVariants = cva(
  'grid w-fit items-start gap-[var(--spacing-mira-md)] rounded-r-sm px-[var(--spacing-mira-md)] py-[var(--spacing-mira-md)] [grid-template-columns:var(--columns-template)] md:w-auto',
  {
    variants: {
      type: {
        damage: 'mira-type-damage bg-[var(--mira-bg-light)]',
        service: 'mira-type-service bg-[var(--mira-bg-light)]',
        suggestions: 'mira-type-suggestions bg-[var(--mira-bg-light)]',
      },
    },
  }
);

const cellVariants = cva(
  'flex min-w-0 items-start text-sm font-normal leading-5 text-[var(--color-text-primary)]',
  {
    variants: {
      align: {
        left: 'justify-start',
        center: 'justify-center',
        right: 'justify-end',
      },
    },
    defaultVariants: {
      align: 'left',
    },
  }
);

const formatDimensionValue = (value: number | string) =>
  typeof value === 'number' ? `${value}px` : value;

/**
 * Gets inline styles for layout dimensions
 * Using inline styles instead of dynamic Tailwind classes for TW4 compatibility
 */
const getLayoutStyles = (props: {
  flex?: number | string;
  minWidth?: number | string;
  width?: number | string;
}): CSSProperties | undefined => {
  const styles: CSSProperties = {};

  if (props.width !== undefined) {
    styles.width = formatDimensionValue(props.width);
  } else if (props.minWidth !== undefined) {
    styles.minWidth = formatDimensionValue(props.minWidth);
  }

  return Object.keys(styles).length > 0 ? styles : undefined;
};

const buildColumnsTemplate = (children: ReactNode): string => {
  const columns: string[] = [];

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) {
      return;
    }

    const props = child.props as {
      width?: number | string;
      minWidth?: number | string;
      flex?: number | string;
    };

    if (props.width !== undefined) {
      columns.push(formatDimensionValue(props.width));
      return;
    }

    if (props.minWidth !== undefined) {
      const minW = formatDimensionValue(props.minWidth);
      if (props.flex !== undefined) {
        columns.push(`minmax(${minW}, ${props.flex}fr)`);
      } else {
        columns.push(minW);
      }
      return;
    }

    if (props.flex !== undefined) {
      columns.push(`minmax(150px, ${props.flex}fr)`);
      return;
    }

    columns.push('auto');
  });

  return columns.join(' ');
};

interface TableProps {
  readonly children: ReactNode;
}

const isHeadElement = (child: ReactNode): child is React.ReactElement<HeadProps> =>
  isValidElement(child) && child.type === Head;

interface HeadProps {
  readonly children: ReactNode;
}

interface BodyProps {
  readonly children: ReactNode;
}

interface HeaderCellProps extends VariantProps<typeof headerCellVariants> {
  readonly children: ReactNode;
  readonly flex?: number | string;
  readonly minWidth?: number | string;
  readonly width?: number | string;
  readonly isFirst?: boolean;
}

interface RowProps {
  readonly type: MiraType;
  readonly icon?: ReactNode;
  readonly children: ReactNode;
}

interface CellProps extends VariantProps<typeof cellVariants> {
  readonly children: ReactNode;
  readonly flex?: number | string;
  readonly minWidth?: number | string;
  readonly width?: number | string;
  readonly isPrimary?: boolean;
}

// ----------------- COMPONENTS -----------------

const TableRoot = ({children}: TableProps) => {
  const columnsTemplate = useMemo(() => {
    let template = '';

    Children.forEach(children, (child) => {
      if (!isHeadElement(child)) {
        return;
      }

      template = buildColumnsTemplate(child.props.children);
    });

    return template;
  }, [children]);

  const style: CSSProperties | undefined = columnsTemplate
    ? ({'--columns-template': columnsTemplate} as CSSProperties)
    : undefined;

  return (
    <div className={viewportVariants()}>
      <div role="table" className={tableVariants()} style={style}>
        {children}
      </div>
    </div>
  );
};

const Head = ({children}: HeadProps) => (
  <div role="rowgroup" className={headVariants()}>
    <div className={headerSpacerVariants()} />
    <div className={headerCellsWrapperVariants()}>{children}</div>
  </div>
);

Head.displayName = 'Table.Head';

const Body = ({children}: BodyProps) => (
  <div role="rowgroup" className={bodyVariants()}>
    {children}
  </div>
);

Body.displayName = 'Table.Body';

const HeaderCell = ({children, align, flex, minWidth, width, isFirst}: HeaderCellProps) => (
  <div
    role="columnheader"
    className={cn(
      headerCellVariants({align}),
      isFirst && 'sticky left-0 z-10 bg-[var(--color-bg-primary)] md:static md:bg-transparent'
    )}
    style={getLayoutStyles({flex, minWidth, width})}
  >
    {children}
  </div>
);

const Row = ({type, icon, children}: RowProps) => (
  <div role="row" className={rowVariants()}>
    {icon && <div className={iconWrapperVariants({type})}>{icon}</div>}
    <div className={cellsWrapperVariants({type})}>{children}</div>
  </div>
);

const Cell = ({children, align, flex, minWidth, width, isPrimary}: CellProps) => (
  <div
    role="cell"
    className={cn(
      cellVariants({align}),
      isPrimary &&
        'sticky left-0 z-10 bg-[var(--mira-bg-light)] md:static md:bg-transparent text-[var(--color-mira-blue-100)] font-medium'
    )}
    style={getLayoutStyles({flex, minWidth, width})}
  >
    {children}
  </div>
);

const Table = Object.assign(TableRoot, {
  Head,
  Body,
  HeaderCell,
  Row,
  Cell,
});

export {Table};
